import PptxGenJS from 'pptxgenjs';

import { RISK_COLUMNS } from 'src/modals/PreferencesModal/RiskColumnSelector';
import { normalizeIabTopExclusionRules } from 'src/apis/preference';
import brandWaveArt from 'src/assets/landing/advertisers.png';
import {
  AlignmentSummaryGenreExceptionRule,
  AlignmentSummaryItem,
  AlignmentSummaryPolicy,
  AlignmentSummaryResponse,
} from 'src/types/campaignReport';

/** Charcoal from the title-slide mock (not pure black). */
export const REPORT_TITLE_BG = '212124';

const SLIDE_WIDTH = 13.333;
const SLIDE_HEIGHT = 7.5;
const FONT = 'Arial';
const TEXT_DARK = '101828';
const TEXT_BLUE = '1D4E89';
const GREEN_RISK = '2E7D32';
const MONO_FONT = 'Courier New';
const MATCH_TEXT = 'D9D7E8';
const MATCH_IMPRESSIONS = '8A81FF';
const MATCH_EPISODES_SHOWS = 'FFC4C4';

type ReportSlide = ReturnType<PptxGenJS['addSlide']>;

export type CampaignReportPptxInput = {
  brandName: string;
  monthLabel: string;
  summary: AlignmentSummaryResponse;
};

type WaveStroke = {
  color: string;
  width: number;
  yOffset: number;
  amp: number;
  phase: number;
};

/**
 * Soft horizontal wave pattern matching the title-slide artwork.
 * Drawn on canvas so the PPT embeds a PNG (broad PowerPoint support).
 */
const createTitleWavePatternDataUrl = (width = 1920, height = 1080): string => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  const strokes: WaveStroke[] = [
    {
      color: 'rgba(196, 120, 140, 0.45)',
      width: 1.4,
      yOffset: -70,
      amp: 48,
      phase: 0.2,
    },
    {
      color: 'rgba(170, 100, 130, 0.38)',
      width: 1.2,
      yOffset: -50,
      amp: 42,
      phase: 0.6,
    },
    {
      color: 'rgba(150, 95, 125, 0.35)',
      width: 1.2,
      yOffset: -30,
      amp: 46,
      phase: 1.1,
    },
    {
      color: 'rgba(130, 110, 140, 0.32)',
      width: 1.1,
      yOffset: -10,
      amp: 40,
      phase: 1.5,
    },
    {
      color: 'rgba(110, 125, 150, 0.30)',
      width: 1.1,
      yOffset: 10,
      amp: 44,
      phase: 0.4,
    },
    {
      color: 'rgba(100, 140, 155, 0.28)',
      width: 1.2,
      yOffset: 28,
      amp: 38,
      phase: 0.9,
    },
    {
      color: 'rgba(120, 130, 155, 0.26)',
      width: 1.0,
      yOffset: 46,
      amp: 42,
      phase: 1.3,
    },
    {
      color: 'rgba(140, 110, 135, 0.30)',
      width: 1.1,
      yOffset: 64,
      amp: 36,
      phase: 0.1,
    },
    {
      color: 'rgba(160, 105, 125, 0.28)',
      width: 1.0,
      yOffset: 82,
      amp: 40,
      phase: 0.7,
    },
    {
      color: 'rgba(175, 115, 130, 0.25)',
      width: 1.0,
      yOffset: 100,
      amp: 34,
      phase: 1.4,
    },
  ];

  const midY = height * 0.48;

  strokes.forEach((stroke) => {
    ctx.beginPath();
    ctx.lineWidth = stroke.width;
    ctx.strokeStyle = stroke.color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let x = 0; x <= width; x += 4) {
      const t = x / width;
      const y =
        midY +
        stroke.yOffset +
        Math.sin(t * Math.PI * 1.15 + stroke.phase) * stroke.amp +
        Math.sin(t * Math.PI * 2.4 + stroke.phase * 1.7) * (stroke.amp * 0.28);
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  });

  return canvas.toDataURL('image/png');
};

const loadImageElement = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

/**
 * Brand strand-bundle wave art (same asset the landing page uses), mirrored
 * horizontally so the bundle sweeps downward to the right. Kept to the lower
 * right and faded so it never competes with tables or list text; renders
 * nothing when the asset is unavailable.
 */
const createBrandWaveDataUrl = async (
  width = 1920,
  height = 1080
): Promise<string> => {
  const image = await loadImageElement(brandWaveArt);
  if (!image) {
    return '';
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  const drawX = width * 0.42;
  const drawW = width * 0.64;
  const drawY = height * 0.3;
  const drawH = height * 0.7;

  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.translate(width, 0);
  ctx.scale(-1, 1);
  // Mirrored space: screen x of `drawX + drawW` maps to `width - (drawX + drawW)`.
  ctx.drawImage(image, width - (drawX + drawW), drawY, drawW, drawH);
  ctx.restore();

  return canvas.toDataURL('image/png');
};

/**
 * Campaign divider background:
 * - 6 evenly spaced horizontal rules, vertically centered
 * - 3 full-width sine curves (~1.7–2 cycles), staggered phases so left
 *   ends differ; trough-to-peak height ~60–80% of slide, bottoms near
 *   the slide bottom with a small margin
 */
const createDividerWavePatternDataUrl = (
  width = 1920,
  height = 1080
): string => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  const midY = height * 0.5;

  // 6 horizontal lines, vertically centered, even spacing.
  const lineGap = height * 0.065;
  ctx.strokeStyle = 'rgba(175, 170, 185, 0.45)';
  ctx.lineWidth = 1.1;
  for (let i = -2.5; i <= 2.5; i += 1) {
    const y = midY + i * lineGap;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  // Peak heights differ (60% / 70% / 80%); ~2 cycles; staggered left starts.
  const bottomY = height * 0.9;
  const curves = [
    {
      peakHeight: height * 0.6,
      stroke: 'rgba(185, 145, 195, 0.55)',
      lineWidth: 2.2,
      cycles: 1.7,
      phase: 0.4,
    },
    {
      peakHeight: height * 0.7,
      stroke: 'rgba(165, 140, 200, 0.50)',
      lineWidth: 2.0,
      cycles: 1.85,
      phase: 1.9,
    },
    {
      peakHeight: height * 0.8,
      stroke: 'rgba(195, 140, 175, 0.48)',
      lineWidth: 2.0,
      cycles: 1.95,
      phase: 3.2,
    },
  ];

  const curveY = (
    x: number,
    peakHeight: number,
    cycles: number,
    phase: number
  ): number => {
    const amp = peakHeight / 2;
    const t = x / width;
    return bottomY - amp + amp * Math.sin(t * Math.PI * 2 * cycles + phase);
  };

  curves.forEach((curve) => {
    // Stroke only — no soft fill ribbon (avoids a glow look).
    ctx.beginPath();
    ctx.lineWidth = curve.lineWidth;
    ctx.strokeStyle = curve.stroke;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let x = 0; x <= width; x += 2) {
      const y = curveY(x, curve.peakHeight, curve.cycles, curve.phase);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  });

  return canvas.toDataURL('image/png');
};

const createGradientBarDataUrl = (
  colorStart: string,
  colorEnd: string,
  width = 600,
  height = 24
): string => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  const gradient = ctx.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  return canvas.toDataURL('image/png');
};

/** Page number (bottom center) + "barometer" (bottom right) on content slides. */
const FOOTER_TOP = SLIDE_HEIGHT - 0.5;

const addSlideFooter = (slide: ReportSlide, pageNumber: number) => {
  slide.addText(String(pageNumber), {
    x: 0,
    y: FOOTER_TOP,
    w: SLIDE_WIDTH,
    h: 0.3,
    fontSize: 12,
    fontFace: FONT,
    color: '101828',
    align: 'center',
    valign: 'middle',
    margin: 0,
  });

  slide.addText('barometer', {
    x: SLIDE_WIDTH - 2.2,
    y: FOOTER_TOP,
    w: 1.8,
    h: 0.3,
    fontSize: 12,
    fontFace: FONT,
    color: '101828',
    align: 'right',
    valign: 'middle',
    margin: 0,
  });
};

const addContentSlideChrome = (
  pptx: PptxGenJS,
  pageNumber: number,
  waveData: string
): ReportSlide => {
  const slide = pptx.addSlide();
  slide.background = { color: 'FFFFFF' };

  if (waveData) {
    slide.addImage({
      data: waveData,
      x: 0,
      y: 0,
      w: '100%',
      h: '100%',
    });
  }

  addSlideFooter(slide, pageNumber);
  return slide;
};

const formatPct = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}%`;
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString('en-US');
};

/** Format a campaign date string as `2026/7/13` (no zero-padding). */
const formatDividerDate = (raw?: string): string => {
  const value = raw?.trim();
  if (!value) return '';

  const match = value.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (match) {
    return `${Number(match[1])}/${Number(match[2])}/${Number(match[3])}`;
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}/${
      parsed.getMonth() + 1
    }/${parsed.getDate()}`;
  }

  return value;
};

/** Date line: `2026/7/13 - 2026/7/19`. */
const formatDividerDateRange = (item: AlignmentSummaryItem): string => {
  const start = formatDividerDate(item.campaign_start);
  const end = formatDividerDate(item.campaign_end);
  if (start && end) {
    return start === end ? start : `${start} - ${end}`;
  }
  return start || end || '';
};

const getPublisherName = (item: AlignmentSummaryItem): string =>
  item.publisher && item.publisher.toLowerCase() !== 'unknown'
    ? item.publisher
    : item.campaign_name;

/** Summary-table first cell: campaign name with date on the next line. */
const campaignNameDateCell = (
  item: AlignmentSummaryItem
): PptxGenJS.TableCell => {
  const name = item.campaign_name?.trim() || getPublisherName(item);
  const dateRange = formatDividerDateRange(item);
  const lines: PptxGenJS.TextProps[] = [
    {
      text: name,
      options: { bold: true, breakLine: Boolean(dateRange) },
    },
  ];
  if (dateRange) {
    lines.push({
      text: dateRange,
      options: { bold: false, fontSize: 9 },
    });
  }
  return { text: lines, options: { align: 'left', valign: 'middle' } };
};

/** Canonical GARM row order from show_scores columns. */
const GARM_CATEGORY_KEYS = RISK_COLUMNS.filter((column) =>
  column.startsWith('show_scores_')
);

const toSlideCategoryLabel = (riskKey: string): string => {
  return riskKey
    .replace(/^show_scores_/, '')
    .replace(/^episode_scores_/, '')
    .replace(/\s*Risk$/i, '')
    .trim();
};

const formatRiskLevel = (level?: string): string => {
  if (!level) return '—';
  const trimmed = level.trim();
  if (/risk$/i.test(trimmed)) return trimmed;
  return `${trimmed} Risk`;
};

const riskLevelColor = (level?: string): string => {
  const normalized = (level || '').toLowerCase();
  if (normalized === 'medium') return GREEN_RISK;
  return TEXT_DARK;
};

const patternToReadable = (pattern: string): string => {
  return pattern
    .replace(/\\b/g, '')
    .replace(/[\\^$*+?.()|[\]{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeLanguagePolicy = (
  raw: AlignmentSummaryPolicy['language_policy']
): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter(
      (code): code is string => typeof code === 'string' && !!code
    );
  }
  if (raw && typeof raw === 'object' && raw.english_only) {
    return ['en'];
  }
  return [];
};

const isEnglishOnlyLanguagePolicy = (languages: string[]): boolean =>
  languages.length > 0 && languages.every((code) => code === 'en');

const getPolicyExceptionRules = (
  policy: AlignmentSummaryPolicy
): AlignmentSummaryGenreExceptionRule[] => [
  ...(policy.genre_exception_rules || []),
  ...(policy.genre_exclusion_bypass_rules || []),
];

const buildConsideration = (
  categoryKey: string,
  policy: AlignmentSummaryPolicy
): string => {
  const parts: string[] = [];

  getPolicyExceptionRules(policy).forEach((rule) => {
    const category = (rule.risk_category || '').toLowerCase();
    const label = toSlideCategoryLabel(categoryKey).toLowerCase();
    if (category && (category.includes(label) || label.includes(category))) {
      const genre = rule.genre || rule.allow_genre || rule.allow || '';
      const level = formatRiskLevel(rule.risk_level);
      parts.push(genre ? `${level} (${genre} OK)` : level);
    }
  });

  (policy.genre_risk_overrides || []).forEach((override) => {
    const columns = override.columns || [];
    if (!columns.length || !override.pattern) return;
    const matches = columns.some(
      (column) =>
        column === categoryKey ||
        column === categoryKey.replace('show_scores_', 'episode_scores_') ||
        toSlideCategoryLabel(column) === toSlideCategoryLabel(categoryKey)
    );
    if (!matches) return;
    const readable = patternToReadable(override.pattern || '');
    const level = formatRiskLevel(override.max_risk);
    parts.push(readable ? `${level} (${readable} OK)` : level);
  });

  return parts.join('; ');
};

const addTitleSlide = (
  pptx: PptxGenJS,
  {
    brandName,
    monthLabel,
  }: Pick<CampaignReportPptxInput, 'brandName' | 'monthLabel'>,
  titleWaveData: string
) => {
  const slide = pptx.addSlide();
  slide.background = { color: REPORT_TITLE_BG };

  if (titleWaveData) {
    slide.addImage({
      data: titleWaveData,
      x: 0,
      y: 0,
      w: '100%',
      h: '100%',
    });
  }

  slide.addText('barometer', {
    x: 0.5,
    y: 2.85,
    w: 12.333,
    h: 0.95,
    fontSize: 60,
    fontFace: FONT,
    bold: true,
    color: 'FFFFFF',
    align: 'center',
    valign: 'middle',
    margin: 0,
  });

  slide.addText(`${brandName.trim()} ${monthLabel} Reporting`, {
    x: 0.5,
    y: 3.85,
    w: 12.333,
    h: 0.5,
    fontSize: 22,
    fontFace: FONT,
    bold: true,
    color: 'FFFFFF',
    align: 'center',
    valign: 'middle',
    margin: 0,
  });
};

const addAnalysisProcessSlide = (pptx: PptxGenJS, waveData: string) => {
  const slide = addContentSlideChrome(pptx, 2, waveData);

  slide.addText('How does this analysis process work?', {
    x: 0.6,
    y: 0.35,
    w: 12,
    h: 0.55,
    fontSize: 28,
    fontFace: FONT,
    bold: true,
    color: '101828',
    margin: 0,
  });

  slide.addText(
    'Three simple steps for brand suitability, context & host data campaign reporting.',
    {
      x: 0.6,
      y: 0.95,
      w: 11.5,
      h: 0.4,
      fontSize: 14,
      fontFace: FONT,
      color: '344054',
      margin: 0,
    }
  );

  const cards: Array<{
    title: string;
    barStart: string;
    barEnd: string;
    body: PptxGenJS.TextProps[];
  }> = [
    {
      title: 'Step 1',
      barStart: '#F4C4A8',
      barEnd: '#E8A0B8',
      body: [
        { text: 'Identify ', options: { color: '101828' } },
        {
          text: 'brand standards',
          options: { color: 'E8916A', bold: true },
        },
        {
          text: ' and operationalize them for each category being measured',
          options: { color: '101828' },
        },
      ],
    },
    {
      title: 'Step 2',
      barStart: '#E8A0B8',
      barEnd: '#C4A0D8',
      body: [
        { text: 'Run the campaign and ', options: { color: '101828' } },
        {
          text: 'obtain signals',
          options: { color: 'D978A0', bold: true },
        },
        {
          text: ' (# impressions, show & episode) on where your ads ran',
          options: { color: '101828' },
        },
      ],
    },
    {
      title: 'Step 3',
      barStart: '#C4A0D8',
      barEnd: '#90B4E8',
      body: [
        {
          text: 'Compare Step 1 standards with Barometer data on Step 2 episodes & shows to ',
          options: { color: '101828' },
        },
        {
          text: 'calculate alignment.',
          options: { color: '7BA3E0', bold: true },
        },
      ],
    },
  ];

  const cardWidth = 3.85;
  const cardHeight = 4.2;
  const cardY = 1.7;
  const gap = 0.35;
  const startX = 0.7;

  cards.forEach((card, index) => {
    const x = startX + index * (cardWidth + gap);

    slide.addShape(pptx.ShapeType.roundRect, {
      x,
      y: cardY,
      w: cardWidth,
      h: cardHeight,
      fill: { color: 'FFFFFF' },
      shadow: {
        type: 'outer',
        color: '101828',
        blur: 12,
        offset: 3,
        opacity: 0.1,
      },
      rectRadius: 0.1,
    });

    const barData = createGradientBarDataUrl(card.barStart, card.barEnd);
    if (barData) {
      slide.addImage({
        data: barData,
        x: x + 0.18,
        y: cardY + 0.22,
        w: cardWidth - 0.36,
        h: 0.08,
      });
    }

    slide.addText(card.title, {
      x: x + 0.28,
      y: cardY + 0.55,
      w: cardWidth - 0.56,
      h: 0.45,
      fontSize: 22,
      fontFace: FONT,
      bold: true,
      color: '101828',
      margin: 0,
    });

    slide.addText(card.body, {
      x: x + 0.28,
      y: cardY + 1.2,
      w: cardWidth - 0.56,
      h: 2.5,
      fontSize: 15,
      fontFace: FONT,
      valign: 'top',
      margin: 0,
    });
  });
};

const addPolicyProfileSlide = (
  pptx: PptxGenJS,
  waveData: string,
  policy: AlignmentSummaryPolicy | undefined
) => {
  const slide = addContentSlideChrome(pptx, 3, waveData);
  const policyTitle = policy?.title?.trim() || 'Policy';

  slide.addText(`${policyTitle} Brand Standard Profile`, {
    x: 0.45,
    y: 0.28,
    w: 12.4,
    h: 0.45,
    fontSize: 24,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const thresholds = policy?.risk_thresholds || {};
  const tableRows: PptxGenJS.TableRow[] = [
    [
      {
        text: 'GARM Category',
        options: { bold: true, color: TEXT_BLUE, align: 'left' },
      },
      {
        text: 'Max tolerated Risk Level',
        options: { bold: true, color: TEXT_DARK, align: 'left' },
      },
      {
        text: 'Consideration',
        options: { bold: true, color: TEXT_DARK, align: 'left' },
      },
    ],
  ];

  GARM_CATEGORY_KEYS.forEach((showKey) => {
    const episodeKey = showKey.replace('show_scores_', 'episode_scores_');
    const level = thresholds[showKey] || thresholds[episodeKey];
    if (!level) return;

    const consideration = policy ? buildConsideration(showKey, policy) : '';
    tableRows.push([
      {
        text: toSlideCategoryLabel(showKey),
        options: { color: TEXT_BLUE, align: 'left', bold: true },
      },
      {
        text: formatRiskLevel(level),
        options: { color: riskLevelColor(level), align: 'left' },
      },
      {
        text: consideration || '',
        options: { color: TEXT_DARK, align: 'left', fontSize: 9 },
      },
    ]);
  });

  const contentTopY = 0.85;
  const tableBorder: PptxGenJS.BorderProps = { pt: 0.75, color: '98A2B3' };
  slide.addTable(tableRows, {
    x: 0.4,
    y: contentTopY,
    w: 7.6,
    colW: [2.9, 2.0, 2.7],
    border: [tableBorder, tableBorder, tableBorder, tableBorder],
    fontFace: FONT,
    fontSize: 10,
    color: TEXT_DARK,
    valign: 'middle',
    align: 'left',
  });

  const rightX = 8.3;
  const chipW = 2.55;
  const chipsAreaW = 4.55;
  const noteX = rightX + 2.7;
  const noteW = 1.9;
  const rightBottom = SLIDE_HEIGHT - 0.55;

  const languages = normalizeLanguagePolicy(policy?.language_policy);
  const englishOnly = isEnglishOnlyLanguagePolicy(languages);
  const foreignBlockH = englishOnly ? 1.35 : 1.0;
  const iabBlockH = 1.55;
  const foreignStartY = rightBottom - iabBlockH - foreignBlockH;
  const genreChipsMaxY = foreignStartY - 0.12;

  // Header + side-note band; chips always start below so 2-col layout never covers the note.
  const genreSectionTopY = contentTopY;
  const genreHeaderBandH = 0.55;
  const genreChipsStartY = genreSectionTopY + genreHeaderBandH;

  const addExclusionChips = (
    items: string[],
    startY: number,
    maxY: number
  ): void => {
    if (items.length === 0) {
      slide.addText('None', {
        x: rightX,
        y: startY,
        w: chipW,
        h: 0.25,
        fontSize: 11,
        fontFace: FONT,
        color: '667085',
        margin: 0,
      });
      return;
    }

    const availableH = Math.max(maxY - startY, 0.25);
    let chipH = 0.26;
    let cols = 1;
    let fontSize = 11;

    const rowsNeeded = (colCount: number, height: number) =>
      Math.ceil(items.length / colCount) * height;

    if (rowsNeeded(1, chipH) > availableH) {
      cols = 2;
    }
    while (rowsNeeded(cols, chipH) > availableH && chipH > 0.18) {
      chipH -= 0.02;
      fontSize = chipH < 0.22 ? 9 : 10;
    }

    const maxRows = Math.max(1, Math.floor(availableH / chipH));
    const maxVisible = maxRows * cols;
    const visible = items.slice(0, maxVisible);
    const overflow = items.length - visible.length;
    if (overflow > 0 && visible.length > 0) {
      visible[visible.length - 1] = `+${overflow + 1} more`;
    }

    const colGap = 0.06;
    const colW = cols === 1 ? chipW : (chipsAreaW - colGap * (cols - 1)) / cols;

    visible.forEach((item, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = rightX + col * (colW + colGap);
      const y = startY + row * chipH;

      slide.addShape(pptx.ShapeType.roundRect, {
        x,
        y,
        w: colW,
        h: chipH,
        fill: { color: 'FFFFFF' },
        line: { color: 'D0D5DD', pt: 0.5 },
        rectRadius: 0.02,
      });
      slide.addText(item, {
        x: x + 0.06,
        y,
        w: colW - 0.1,
        h: chipH,
        fontSize,
        fontFace: FONT,
        color: TEXT_DARK,
        valign: 'middle',
        margin: 0,
      });
    });
  };

  // Genre exclusions — header and side note share the same top y.
  slide.addText('Genre exclusions', {
    x: rightX,
    y: genreSectionTopY,
    w: chipW,
    h: 0.3,
    fontSize: 13,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });
  addExclusionChips(
    policy?.genre_exclusions || [],
    genreChipsStartY,
    genreChipsMaxY
  );
  // Draw after chips so the note stays visible above any column layout.
  slide.addText('Genres never OK no matter show scores', {
    x: noteX,
    y: genreSectionTopY,
    w: noteW,
    h: genreHeaderBandH,
    fontSize: 10,
    fontFace: FONT,
    color: '667085',
    valign: 'top',
    margin: 0,
  });

  slide.addText(
    'Foreign language - we can apply your standards to any language. Let us know if you are planning on including foreign language content. If not, we can also tell you when it\'s included and shouldn\'t be.',
    {
      x: rightX,
      y: foreignStartY,
      w: 4.5,
      h: 0.85,
      fontSize: 10,
      fontFace: FONT,
      bold: true,
      color: TEXT_DARK,
      margin: 0,
    }
  );

  let afterForeignY = foreignStartY + 0.9;
  if (englishOnly) {
    slide.addText('Only English speaking shows allowed', {
      x: rightX,
      y: afterForeignY,
      w: 4.5,
      h: 0.28,
      fontSize: 11,
      fontFace: FONT,
      italic: true,
      color: TEXT_DARK,
      margin: 0,
    });
    afterForeignY += 0.35;
  }

  // IAB exclusions — header and side note share the same top y; items as comma-separated text.
  const iabSectionTopY = afterForeignY + 0.08;
  slide.addText('IAB Exclusions', {
    x: rightX,
    y: iabSectionTopY,
    w: chipW,
    h: 0.3,
    fontSize: 13,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });
  slide.addText('IAB Categories never OK no matter show scores', {
    x: noteX,
    y: iabSectionTopY,
    w: noteW,
    h: 0.9,
    fontSize: 10,
    fontFace: FONT,
    color: '667085',
    margin: 0,
  });

  const iabRules = normalizeIabTopExclusionRules(
    policy?.iab_top_exclusion_rules
  );
  slide.addText(iabRules.length ? iabRules.join(', ') : 'None', {
    x: rightX,
    y: iabSectionTopY + 0.32,
    w: chipW,
    h: Math.max(rightBottom - (iabSectionTopY + 0.32), 0.4),
    fontSize: 11,
    fontFace: FONT,
    color: TEXT_DARK,
    valign: 'top',
    margin: 0,
  });
};

const addBrandSuitabilitySummarySlide = (
  pptx: PptxGenJS,
  waveData: string,
  items: AlignmentSummaryItem[]
) => {
  const slide = addContentSlideChrome(pptx, 4, waveData);

  slide.addText('Summary Data - Brand Suitability Alignment', {
    x: 0.45,
    y: 0.3,
    w: 12.4,
    h: 0.45,
    fontSize: 24,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const rows: PptxGenJS.TableRow[] = [
    [
      { text: 'Publisher', options: { bold: true } },
      { text: '% Show Count Aligned', options: { bold: true } },
      { text: '% Show Impressions Aligned', options: { bold: true } },
      { text: '% Episode Count Aligned', options: { bold: true } },
      { text: '% Impressions Aligned', options: { bold: true } },
    ],
  ];

  items.forEach((item) => {
    const alignment = item.brand_suitability_alignment;
    rows.push([
      campaignNameDateCell(item),
      { text: formatPct(alignment.show_count_aligned_pct) },
      { text: formatPct(alignment.show_impressions_aligned_pct) },
      { text: formatPct(alignment.episode_count_aligned_pct) },
      { text: formatPct(alignment.episode_impressions_aligned_pct) },
    ]);
  });

  const tableBorder: PptxGenJS.BorderProps = { pt: 1, color: '000000' };
  slide.addTable(rows, {
    x: 0.55,
    y: 1.2,
    w: 12.2,
    colW: [3.8, 2.1, 2.3, 2.1, 1.9],
    border: [tableBorder, tableBorder, tableBorder, tableBorder],
    fontFace: FONT,
    fontSize: 11,
    color: TEXT_DARK,
    align: 'center',
    valign: 'middle',
  });
};

const addInclusionListSummarySlide = (
  pptx: PptxGenJS,
  waveData: string,
  items: AlignmentSummaryItem[]
) => {
  const slide = addContentSlideChrome(pptx, 5, waveData);

  slide.addText('Summary Data- Inclusion List Alignment', {
    x: 0.45,
    y: 0.3,
    w: 12.4,
    h: 0.45,
    fontSize: 24,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const rows: PptxGenJS.TableRow[] = [
    [
      { text: 'Publisher', options: { bold: true } },
      { text: 'Total Shows Delivered', options: { bold: true } },
      { text: 'Unique Shows on Inclusion List', options: { bold: true } },
      { text: 'Unique Shows Not on Inclusion List', options: { bold: true } },
      { text: '% Shows on Inclusion List', options: { bold: true } },
      { text: 'Impressions on Inclusion List', options: { bold: true } },
    ],
  ];

  items.forEach((item) => {
    const inclusion = item.inclusion_list_alignment;
    rows.push([
      campaignNameDateCell(item),
      { text: formatNumber(inclusion.total_shows_delivered) },
      { text: formatNumber(inclusion.unique_shows_on_list) },
      { text: formatNumber(inclusion.unique_shows_not_on_list) },
      { text: formatPct(inclusion.shows_on_list_pct) },
      { text: formatNumber(inclusion.impressions_on_list) },
    ]);
  });

  const tableBorder: PptxGenJS.BorderProps = { pt: 1, color: '000000' };
  slide.addTable(rows, {
    x: 0.35,
    y: 1.2,
    w: 12.6,
    colW: [3.0, 1.6, 2.05, 2.2, 1.8, 1.95],
    border: [tableBorder, tableBorder, tableBorder, tableBorder],
    fontFace: FONT,
    fontSize: 10,
    color: TEXT_DARK,
    align: 'center',
    valign: 'middle',
  });
};

const truncateText = (text: string, maxChars: number): string => {
  if (text.length <= maxChars) return text;
  if (maxChars <= 1) return '…';
  return `${text.slice(0, maxChars - 1).trimEnd()}…`;
};

/** Estimate mono wrapped height and shrink/truncate until it fits the box. */
const fitMonoListBody = (
  lines: string[],
  boxWidthIn: number,
  boxHeightIn: number
): { fontSize: number; lines: string[] } => {
  const minFont = 8;
  let fontSize = 12;
  const working = lines.map((line) => line);

  const measureHeight = (fs: number, texts: string[]): number => {
    // Courier average glyph width ≈ 0.6em.
    const charWidthIn = (fs / 72) * 0.6;
    const charsPerLine = Math.max(28, Math.floor(boxWidthIn / charWidthIn));
    const lineHeightIn = (fs / 72) * 1.22;
    const wrapped = texts.reduce((count, line) => {
      const len = Math.max(line.length, 1);
      return count + Math.ceil(len / charsPerLine);
    }, 0);
    return wrapped * lineHeightIn;
  };

  while (fontSize > minFont && measureHeight(fontSize, working) > boxHeightIn) {
    fontSize -= 0.5;
  }

  let guard = 0;
  while (measureHeight(fontSize, working) > boxHeightIn && guard < 60) {
    let longestIdx = 0;
    for (let i = 1; i < working.length; i += 1) {
      if (working[i].length > working[longestIdx].length) longestIdx = i;
    }
    if (working[longestIdx].length <= 48) break;
    working[longestIdx] = truncateText(
      working[longestIdx],
      Math.floor(working[longestIdx].length * 0.88)
    );
    guard += 1;
  }

  return { fontSize, lines: working };
};

const addRankedShowListSlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number,
  title: string,
  subtitle: string,
  lines: string[]
) => {
  const slide = addContentSlideChrome(pptx, pageNumber, waveData);

  slide.addText(title, {
    x: 0.55,
    y: 0.28,
    w: 12.2,
    h: 0.42,
    fontSize: 26,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const bodyX = 0.55;
  const bodyY = 0.85;
  const bodyW = 12.2;
  const bodyH = FOOTER_TOP - bodyY - 0.08;
  const fitted = fitMonoListBody([subtitle, ...lines], bodyW, bodyH);

  slide.addText(fitted.lines.join('\n'), {
    x: bodyX,
    y: bodyY,
    w: bodyW,
    h: bodyH,
    fontSize: fitted.fontSize,
    fontFace: MONO_FONT,
    color: TEXT_DARK,
    valign: 'top',
    margin: 0,
  });

  // Re-draw footer above body text so page number stays readable.
  addSlideFooter(slide, pageNumber);
};

/** Slide: campaign name + date range divider (repeats per campaign). */
const addCampaignDividerSlide = (
  pptx: PptxGenJS,
  pageNumber: number,
  item: AlignmentSummaryItem,
  dividerWaveData: string
) => {
  // Wave art already includes the horizontal rules — don't double-draw PPT lines.
  const slide = addContentSlideChrome(pptx, pageNumber, dividerWaveData);

  const name = item.campaign_name?.trim() || getPublisherName(item);
  const dateRange = formatDividerDateRange(item);

  // Single text block so wrapped names push the date down instead of overlapping.
  const lines: PptxGenJS.TextProps[] = [
    {
      text: name,
      options: {
        bold: true,
        fontSize: 32,
        color: '2D2D30',
        breakLine: Boolean(dateRange),
      },
    },
  ];
  if (dateRange) {
    lines.push({
      text: dateRange,
      options: {
        bold: true,
        fontSize: 26,
        color: '2D2D30',
        // Extra space after the name line(s).
        paraSpaceBefore: 14,
      },
    });
  }

  slide.addText(lines, {
    x: 0.8,
    y: 2.4,
    w: 11.7,
    h: 2.2,
    fontFace: FONT,
    align: 'center',
    valign: 'middle',
    margin: 0,
  });
};

/** Dark match-summary slide (repeats per campaign); uses the title wave art. */
const addCampaignMatchSummarySlide = (
  pptx: PptxGenJS,
  pageNumber: number,
  item: AlignmentSummaryItem,
  titleWaveData: string
) => {
  const slide = pptx.addSlide();
  slide.background = { color: REPORT_TITLE_BG };

  if (titleWaveData) {
    slide.addImage({
      data: titleWaveData,
      x: 0,
      y: 0,
      w: '100%',
      h: '100%',
    });
  }

  const match = item.match_summary;
  const impressions = formatNumber(match?.matched_impressions ?? 0);
  const episodes = formatNumber(match?.matched_episodes ?? 0);
  const shows = formatNumber(match?.matched_shows ?? 0);

  slide.addText(
    [
      { text: 'We matched ', options: { color: MATCH_TEXT, bold: true } },
      { text: impressions, options: { color: MATCH_IMPRESSIONS, bold: true } },
      {
        text: ' campaign impressions\nacross ',
        options: { color: MATCH_TEXT, bold: true },
      },
      { text: episodes, options: { color: MATCH_EPISODES_SHOWS, bold: true } },
      { text: ' episodes of ', options: { color: MATCH_TEXT, bold: true } },
      { text: shows, options: { color: MATCH_EPISODES_SHOWS, bold: true } },
      { text: ' shows.', options: { color: MATCH_TEXT, bold: true } },
    ],
    {
      x: 0.8,
      y: 2.6,
      w: 11.7,
      h: 2,
      fontSize: 32,
      fontFace: FONT,
      align: 'center',
      valign: 'middle',
      margin: 0,
    }
  );

  // Light footer so page sequence stays visible on the dark slide.
  slide.addText(String(pageNumber), {
    x: 0,
    y: SLIDE_HEIGHT - 0.45,
    w: SLIDE_WIDTH,
    h: 0.3,
    fontSize: 12,
    fontFace: FONT,
    color: '98A2B3',
    align: 'center',
    margin: 0,
  });
  slide.addText('barometer', {
    x: SLIDE_WIDTH - 2.2,
    y: SLIDE_HEIGHT - 0.45,
    w: 1.8,
    h: 0.3,
    fontSize: 12,
    fontFace: FONT,
    color: '98A2B3',
    align: 'right',
    margin: 0,
  });
};

const addCampaignAlignedShowsSlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number,
  item: AlignmentSummaryItem
) => {
  const shows = item.top_aligned_shows || [];
  const lines = shows.length
    ? shows.map(
        (show) =>
          `${show.rank}. ${show.show_title} – ${formatNumber(show.impressions)}`
      )
    : ['None'];

  addRankedShowListSlide(
    pptx,
    waveData,
    pageNumber,
    'Top Delivering Aligned Titles',
    'Top 10 Aligned Shows by Impression (with impressions):',
    lines
  );
};

const addCampaignMisalignedShowsSlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number,
  item: AlignmentSummaryItem
) => {
  const shows = item.top_misaligned_shows || [];
  const lines = shows.length
    ? shows.map(
        (show) =>
          `${show.rank}. ${show.show_title} – ${formatNumber(show.impressions)}`
      )
    : ['None'];

  addRankedShowListSlide(
    pptx,
    waveData,
    pageNumber,
    'Top Delivering Misaligned Titles',
    'Top 10 Misaligned Shows by Impression (with impressions):',
    lines
  );
};

const addCampaignMisalignedEpisodesSlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number,
  item: AlignmentSummaryItem
) => {
  const episodes = item.top_misaligned_episodes || [];
  const lines = episodes.length
    ? episodes.map((episode) => {
        const pct = formatPct(episode.impressions_pct);
        const violation = episode.violation_reason || '';
        const episodeTitle = truncateText(episode.episode_title || '', 90);
        return `${episode.rank}. ${
          episode.show_title
        } – ${episodeTitle} – ${formatNumber(
          episode.impressions
        )} (${pct}) – Violation: ${violation}`;
      })
    : ['None'];

  addRankedShowListSlide(
    pptx,
    waveData,
    pageNumber,
    'Top Delivering Mis-Aligned Episodes',
    'Top 10 Misaligned Episodes by Impression (with details):',
    lines
  );
};

const addCampaignGenreIabSlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number,
  item: AlignmentSummaryItem
) => {
  const slide = addContentSlideChrome(pptx, pageNumber, waveData);

  slide.addText('Top Delivering Genre & IAB', {
    x: 0.55,
    y: 0.35,
    w: 12.2,
    h: 0.5,
    fontSize: 28,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const genres = item.top_genres || [];
  const iab = item.top_iab_categories || [];

  const genreLines = genres.length
    ? genres.map(
        (genre) =>
          `${genre.rank}. ${genre.name} – ${formatNumber(genre.impressions)}`
      )
    : ['None'];
  const iabLines = iab.length
    ? iab.map(
        (category) =>
          `${category.rank}. ${category.name} – ${formatNumber(
            category.impressions
          )}`
      )
    : ['None'];

  const body = [
    'Top 5 Genres by Impression:',
    ...genreLines.map((line) => `  ${line}`),
    '',
    'Top 5 IAB Categories by Impression:',
    ...iabLines.map((line) => `  ${line}`),
  ].join('\n');

  slide.addText(body, {
    x: 3.2,
    y: 1.4,
    w: 7,
    h: FOOTER_TOP - 1.4 - 0.08,
    fontSize: 14,
    fontFace: MONO_FONT,
    color: TEXT_DARK,
    valign: 'top',
    margin: 0,
  });
  addSlideFooter(slide, pageNumber);
};

/** Slides 6–11 repeated for each campaign item. Returns next page number. */
const addPerCampaignSlides = (
  pptx: PptxGenJS,
  waveData: string,
  dividerWaveData: string,
  titleWaveData: string,
  items: AlignmentSummaryItem[],
  startingPageNumber: number
): number => {
  let pageNumber = startingPageNumber;

  items.forEach((item) => {
    addCampaignDividerSlide(pptx, pageNumber++, item, dividerWaveData);
    addCampaignMatchSummarySlide(pptx, pageNumber++, item, titleWaveData);
    addCampaignAlignedShowsSlide(pptx, waveData, pageNumber++, item);
    addCampaignMisalignedShowsSlide(pptx, waveData, pageNumber++, item);
    addCampaignMisalignedEpisodesSlide(pptx, waveData, pageNumber++, item);
    addCampaignGenreIabSlide(pptx, waveData, pageNumber++, item);
  });

  return pageNumber;
};

/** Closing appendix: reporting key / glossary. */
const addAppendixReportingKeySlide = (
  pptx: PptxGenJS,
  waveData: string,
  pageNumber: number
) => {
  const slide = addContentSlideChrome(pptx, pageNumber, waveData);

  slide.addText('Appendix A', {
    x: 0.55,
    y: 0.28,
    w: 12.2,
    h: 0.42,
    fontSize: 28,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });
  slide.addText('REPORTING KEY', {
    x: 0.55,
    y: 0.7,
    w: 12.2,
    h: 0.32,
    fontSize: 16,
    fontFace: FONT,
    bold: true,
    color: TEXT_DARK,
    margin: 0,
  });

  const body: PptxGenJS.TextProps[] = [
    { text: 'Summary Table:', options: { bold: true, breakLine: true } },
    {
      text: '•  Benchmark: ',
      options: { bold: true },
    },
    {
      text: 'This is based on a combination of historical monitoring campaigns. The benchmark is indicative of campaigns measuring for show alignment, episode alignment and contextual alignment. *** Please note this benchmark is based on 2024 data and has not been updated with 2025 data',
      options: { breakLine: true },
    },
    {
      text: '•  % Show Count Aligned: ',
      options: { bold: true },
    },
    {
      text: 'The percentage of overall show delivery (at the title level) that aligned with the maximum tolerated risk level as indicated by an approved Brand Standards Profile.',
      options: { breakLine: true },
    },
    {
      text: '•  % Show Impressions Aligned: ',
      options: { bold: true },
    },
    {
      text: 'The percentage of titles that aligned with the maximum tolerated risk level as indicated by an approved Brand Standards Profile. (Ie. If 100 shows ran and 90 met your standards, this value is 90%)',
      options: { breakLine: true },
    },
    {
      text: '•  % Episode Count Aligned: ',
      options: { bold: true },
    },
    {
      text: 'The percentage of episodes that aligned with the maximum tolerated risk level as indicated by an approved Brand Standards Profile. (Ie. If 1000 shows ran and 900 met your standards, this value is 90%)',
      options: { breakLine: true },
    },
    {
      text: '•  % Impressions Aligned: ',
      options: { bold: true },
    },
    {
      text: 'The percentage of overall impressions delivered that aligned with the maximum tolerated risk level at the episode level.',
      options: { breakLine: true },
    },
    { text: '', options: { breakLine: true } },
    { text: 'Title Delivery:', options: { bold: true, breakLine: true } },
    {
      text: '•  The number beside top delivering titles refers to the impression count delivered on that title over the indicated week(s) of reporting.',
      options: { breakLine: true },
    },
    { text: '', options: { breakLine: true } },
    { text: 'Platform View:', options: { bold: true, breakLine: true } },
    {
      text: '•  Dark Purple: High Risk',
      options: { breakLine: true },
    },
    {
      text: '•  Moderate Purple: Medium Risk',
      options: { breakLine: true },
    },
    {
      text: '•  Light Purple: Low Risk',
      options: { breakLine: true },
    },
    { text: '', options: { breakLine: true } },
    { text: 'Additional Metrics:', options: { bold: true, breakLine: true } },
    {
      text: '•  Genre: ',
      options: { bold: true },
    },
    {
      text: 'Passed by the publishers',
      options: { breakLine: true },
    },
    {
      text: '•  IAB Category: ',
      options: { bold: true },
    },
    {
      text: 'As indicated by Barometer scored data.',
      options: {},
    },
  ];

  slide.addText(body, {
    x: 0.55,
    y: 1.15,
    w: 12.2,
    h: 5.5,
    fontSize: 11,
    fontFace: FONT,
    color: TEXT_DARK,
    valign: 'top',
    margin: 0,
  });
};

/**
 * Builds and downloads the campaign report PPTX.
 * Slides 1–5 shared, then slides 6–11 repeated for each campaign, then Appendix A.
 */
export const generateCampaignReportPptx = async ({
  brandName,
  monthLabel,
  summary,
}: CampaignReportPptxInput): Promise<void> => {
  const pptx = new PptxGenJS();
  pptx.defineLayout({
    name: 'LAYOUT_WIDE',
    width: SLIDE_WIDTH,
    height: SLIDE_HEIGHT,
  });
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Barometer';
  pptx.title = `${brandName.trim()} ${monthLabel} Reporting`;

  const contentWave = await createBrandWaveDataUrl();
  const dividerWave = createDividerWavePatternDataUrl();
  const titleWave = createTitleWavePatternDataUrl();
  const items = summary.items || [];

  addTitleSlide(pptx, { brandName, monthLabel }, titleWave);
  addAnalysisProcessSlide(pptx, contentWave);
  addPolicyProfileSlide(pptx, contentWave, summary.policy);
  addBrandSuitabilitySummarySlide(pptx, contentWave, items);
  addInclusionListSummarySlide(pptx, contentWave, items);
  const nextPage = addPerCampaignSlides(
    pptx,
    contentWave,
    dividerWave,
    titleWave,
    items,
    6
  );
  addAppendixReportingKeySlide(pptx, contentWave, nextPage);

  await pptx.writeFile({ fileName: 'report.pptx' });
};
